import {
  Box,
  Button,
  Group,
  Image,
  MultiSelect,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Product } from "../../../../gql/__generated__/graphql";
import { PRODUCT_CATEGORIES } from "../../../constants/webData";
import { isValidUrl } from "../../../utils/dataUtils";

// Price validation regex pattern for dollars and cents (e.g., 10.99)
const pricePattern = /^\d+(\.\d{1,2})?$/;

export interface ProductFormValues {
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  categories: string[];
  productUrl: string;
}

interface ProductFormProps {
  initialValues?: Product;
  onSubmit: (values: ProductFormValues) => void;
  onCancel: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

function ProductForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = "Add Product",
  isLoading = false,
}: ProductFormProps) {
  // const renderCount = useRef(0);
  // renderCount.current += 1;
  // console.log(`ProductForm render #${renderCount.current}`);

  const form = useForm<ProductFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      imageUrl: initialValues?.imageUrl || "",
      price: initialValues?.price || ("" as unknown as number),
      categories: initialValues?.categories || [],
      productUrl: initialValues?.productUrl || "",
    },
    // Replace the price validation with this simpler version
    validate: {
      name: (value) =>
        value.trim().length < 3 ? "Name must be at least 3 characters" : null,
      description: (value) =>
        value.trim().length < 10
          ? "Description must be at least 10 characters"
          : null,
      imageUrl: (value) => {
        if (value.trim().length === 0) return "Image URL is required";
        if (!isValidUrl(value)) return "Please enter a valid URL";
        return null;
      },
      price: (value) => {
        // @ts-ignore
        if (value === null || value === undefined || value === "") {
          return "Price is required";
        }

        const numValue = typeof value === "string" ? parseFloat(value) : value;

        if (isNaN(numValue) || numValue < 0.99) {
          return "Price must be at least 0.99";
        }

        return null;
      },
      categories: (value) =>
        value.length === 0
          ? "Select at least one category"
          : value.length > 3
          ? "Maximum 3 categories allowed"
          : null,
      productUrl: (value) => {
        if (value.trim().length === 0) return "Product URL is required";
        if (!isValidUrl(value)) return "Please enter a valid URL";
        return null;
      },
    },
  });

  const handleSubmit = (values: ProductFormValues) => {
    onSubmit(values);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label="Product Name"
          placeholder="Enter product name"
          required
          {...form.getInputProps("name")}
        />

        <TextInput
          label="Description"
          placeholder="Enter product description"
          required
          {...form.getInputProps("description")}
        />

        <TextInput
          label="Image URL"
          placeholder="Enter image URL (https://...)"
          required
          {...form.getInputProps("imageUrl")}
        />

        <NumberInput
          label="Price ($)"
          placeholder="Enter price (e.g., 19.99)"
          step={0.01}
          decimalScale={2}
          required
          hideControls
          {...form.getInputProps("price")}
        />

        <MultiSelect
          label="Categories"
          data={PRODUCT_CATEGORIES}
          placeholder="Select up to 3 categories"
          required
          {...form.getInputProps("categories")}
          onChange={(value) => {
            // Limit to 3 categories
            if (value.length <= 3) {
              form.setFieldValue("categories", value);
            } else {
              // If more than 3 are selected, keep only the first 3
              form.setFieldValue("categories", value.slice(0, 3));
            }
          }}
          error={form.errors.categories}
        />

        <TextInput
          label="Product Link"
          placeholder="Enter product URL (https://...)"
          required
          {...form.getInputProps("productUrl")}
        />

        {form.values.imageUrl && (
          <Box mt="sm">
            <Text size="sm" fw={500} mb="xs">
              Image Preview:
            </Text>
            <Image
              src={form.values.imageUrl}
              alt="Product preview"
              height={200}
              fit="cover"
              fallbackSrc="https://placehold.co/400x200?text=Image+Preview"
              style={{ width: "100%", maxWidth: 400 }}
            />
          </Box>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default ProductForm;
